import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Button,
  Typography,
  Space,
  Select,
  DatePicker,
  Empty,
  Dropdown,
  Divider,
  message,
} from "antd";
import {
  EyeOutlined,
  SelectOutlined,
  DollarOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  LineChartOutlined,
  UserOutlined,
  PercentageOutlined,
  RiseOutlined,
  BarChartOutlined,
  PieChartOutlined,
  TrophyOutlined,
  MobileOutlined,
  DownloadOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import {
  getPromotionAnalytics,
  getPromotionViewsOverTime,
  getPromotionConversionsOverTime,
  getPromotionAudienceBreakdown,
  getPromotionDeviceBreakdown,
  getPromotionGamificationStats,
} from "@api/subscription/promotion";
import { useParams, useHistory } from "react-router-dom";
import { format } from "date-fns";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { motion } from "framer-motion";

// Import analytics components
import {
  PromotionStatsCard,
  PromotionPerformanceChart,
  ConversionFunnel,
  GamificationStats,
  DeviceBreakdown,
  AudienceBreakdown,
} from "./analytics/components";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CHART_COLORS = ["#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96"];

const PromotionAnalytics = () => {
  const { id } = useParams();
  const history = useHistory();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [viewsData, setViewsData] = useState([]);
  const [conversionsData, setConversionsData] = useState([]);
  const [audienceData, setAudienceData] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [gamificationData, setGamificationData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Filters
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [groupBy, setGroupBy] = useState("day");
  const [chartType, setChartType] = useState("area");

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        startDate: dateRange?.[0]?.toDate?.()?.toISOString() || dateRange?.[0]?.toISOString?.(),
        endDate: dateRange?.[1]?.toDate?.()?.toISOString() || dateRange?.[1]?.toISOString?.(),
        groupBy,
      };

      const [overviewRes, viewsRes, conversionsRes, audienceRes, deviceRes, gamificationRes] =
        await Promise.all([
          getPromotionAnalytics(id),
          getPromotionViewsOverTime(id, filters),
          getPromotionConversionsOverTime(id, filters),
          getPromotionAudienceBreakdown(id),
          getPromotionDeviceBreakdown(id).catch(() => ({ data: null })),
          getPromotionGamificationStats(id).catch(() => ({ data: null })),
        ]);

      setOverview(overviewRes.data?.data || overviewRes.data);
      setViewsData(viewsRes.data?.data?.views || viewsRes.data?.views || []);
      setConversionsData(
        conversionsRes.data?.data?.conversions || conversionsRes.data?.conversions || []
      );
      setAudienceData(audienceRes.data?.data || audienceRes.data);
      setDeviceData(deviceRes.data?.data || deviceRes.data);
      setGamificationData(gamificationRes.data?.data || gamificationRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      message.error("Failed to load promotion analytics");
    } finally {
      setLoading(false);
    }
  }, [id, dateRange, groupBy]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    message.success("Analytics refreshed");
  };

  // Go back
  const goBack = () => {
    history.push("/super-admin/promotions/list");
  };

  // Go to dashboard
  const goToDashboard = () => {
    history.push("/super-admin/promotions/analytics");
  };

  // Calculate rates
  const ctr =
    overview?.totalViews > 0
      ? ((overview?.totalClicks || 0) / overview.totalViews) * 100
      : 0;

  const conversionRate =
    overview?.totalClicks > 0
      ? ((overview?.totalConversions || 0) / overview.totalClicks) * 100
      : 0;

  const overallConversionRate =
    overview?.totalViews > 0
      ? ((overview?.totalConversions || 0) / overview.totalViews) * 100
      : 0;

  // Format views data for chart
  const formatViewsData = () => {
    return viewsData.map((item) => ({
      ...item,
      date: format(new Date(item.date), groupBy === "hour" ? "HH:mm" : "MMM d"),
    }));
  };

  // Format conversions data for chart
  const formatConversionsData = () => {
    return conversionsData.map((item) => ({
      ...item,
      date: format(new Date(item.date), groupBy === "hour" ? "HH:mm" : "MMM d"),
    }));
  };

  // Stats cards configuration
  const statsCards = [
    {
      title: "Total Views",
      value: overview?.totalViews || 0,
      icon: <EyeOutlined />,
      color: "#1890ff",
      backgroundColor: "#e6f7ff",
      tooltip: "Total number of times this promotion was viewed",
    },
    {
      title: "Unique Views",
      value: overview?.uniqueViews || 0,
      icon: <UserOutlined />,
      color: "#722ed1",
      backgroundColor: "#f9f0ff",
      tooltip: "Number of unique users who viewed this promotion",
    },
    {
      title: "Total Clicks",
      value: overview?.totalClicks || 0,
      icon: <SelectOutlined />,
      color: "#52c41a",
      backgroundColor: "#f6ffed",
      tooltip: "Total clicks on the promotion CTA",
    },
    {
      title: "Click-Through Rate",
      value: ctr.toFixed(2),
      suffix: "%",
      icon: <PercentageOutlined />,
      color: "#faad14",
      backgroundColor: "#fffbe6",
      tooltip: "Percentage of views that resulted in clicks",
    },
    {
      title: "Conversions",
      value: overview?.totalConversions || 0,
      icon: <DollarOutlined />,
      color: "#13c2c2",
      backgroundColor: "#e6fffb",
      tooltip: "Number of successful conversions",
    },
    {
      title: "Revenue Generated",
      value: (overview?.revenueGenerated || 0).toLocaleString(),
      prefix: "$",
      icon: <RiseOutlined />,
      color: "#eb2f96",
      backgroundColor: "#fff0f6",
      tooltip: "Total revenue generated from this promotion",
    },
  ];

  // Export menu items
  const exportMenuItems = [
    { key: "csv", label: "Export as CSV" },
    { key: "json", label: "Export as JSON" },
  ];

  // Loading state
  if (loading && !overview) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // Render overview tab content
  const renderOverviewTab = () => (
    <>
      {/* Stats Grid */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsCards.map((stat, index) => (
          <Col xs={24} sm={12} md={8} lg={4} key={index}>
            <PromotionStatsCard
              title={stat.title}
              value={stat.value}
              prefix={stat.prefix}
              suffix={stat.suffix}
              icon={stat.icon}
              color={stat.color}
              backgroundColor={stat.backgroundColor}
              tooltip={stat.tooltip}
              loading={loading}
            />
          </Col>
        ))}
      </Row>

      {/* Promotion Details Card */}
      <Card
        title="Promotion Details"
        bordered={false}
        style={{ marginBottom: 24 }}
      >
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Name:</Text>
              <Text strong style={{ marginLeft: 8 }}>
                {overview?.promotionName || "N/A"}
              </Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Type:</Text>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {overview?.type?.replace("_", " ").toUpperCase()}
              </Tag>
              {overview?.gamification?.enabled && (
                <Tag color="magenta">
                  <TrophyOutlined /> GAMIFIED
                </Tag>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Status:</Text>
              <Tag
                color={overview?.isActive ? "green" : "default"}
                style={{ marginLeft: 8 }}
              >
                {overview?.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Discount:</Text>
              <Text strong style={{ marginLeft: 8 }}>
                {overview?.discount?.discountValue || 0}
                {overview?.discount?.discountType === "percentage"
                  ? "%"
                  : ` ${overview?.discount?.currency || "USD"}`}
              </Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Start Date:</Text>
              <Text style={{ marginLeft: 8 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {overview?.schedule?.startDate
                  ? format(new Date(overview.schedule.startDate), "PPP")
                  : "Not set"}
              </Text>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">End Date:</Text>
              <Text style={{ marginLeft: 8 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                {overview?.schedule?.endDate
                  ? format(new Date(overview.schedule.endDate), "PPP")
                  : "Not set"}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Conversion Funnel */}
      <ConversionFunnel
        title="Conversion Funnel"
        views={overview?.totalViews || 0}
        clicks={overview?.totalClicks || 0}
        conversions={overview?.totalConversions || 0}
        revenue={overview?.revenueGenerated || 0}
        loading={loading}
        style={{ marginBottom: 24 }}
      />
    </>
  );

  // Render views tab content
  const renderViewsTab = () => (
    <PromotionPerformanceChart
      title="Views Over Time"
      data={formatViewsData()}
      chartType={chartType}
      metrics={["views", "uniqueViews"]}
      loading={loading}
      onChartTypeChange={setChartType}
      height={400}
    />
  );

  // Render conversions tab content
  const renderConversionsTab = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <PromotionPerformanceChart
          title="Conversions Over Time"
          data={formatConversionsData()}
          chartType="bar"
          metrics={["conversions"]}
          loading={loading}
          height={300}
        />
      </Col>
      <Col span={24}>
        <PromotionPerformanceChart
          title="Revenue Over Time"
          data={formatConversionsData()}
          chartType="area"
          metrics={["revenue"]}
          loading={loading}
          height={300}
        />
      </Col>
    </Row>
  );

  // Render audience tab content
  const renderAudienceTab = () => (
    <AudienceBreakdown
      title="Audience Performance"
      data={audienceData}
      loading={loading}
    />
  );

  // Render devices tab content
  const renderDevicesTab = () => (
    <DeviceBreakdown
      title="Device Analytics"
      data={deviceData}
      loading={loading}
    />
  );

  // Render gamification tab content
  const renderGamificationTab = () => (
    <GamificationStats
      title="Gamification Performance"
      data={gamificationData}
      loading={loading}
    />
  );

  return (
    <Card style={{ borderRadius: 0, minHeight: "100vh" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack}>
              Back to Promotions
            </Button>
            <Divider type="vertical" />
            <Button type="link" onClick={goToDashboard}>
              View Dashboard
            </Button>
          </Space>
        </Col>
        <Col>
          <Space>
            <Select value={groupBy} onChange={setGroupBy} style={{ width: 100 }}>
              <Option value="hour">Hourly</Option>
              <Option value="day">Daily</Option>
              <Option value="week">Weekly</Option>
              <Option value="month">Monthly</Option>
            </Select>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              style={{ width: 280 }}
            />
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
              <Button icon={<DownloadOutlined />}>Export</Button>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      {/* Title */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <LineChartOutlined style={{ marginRight: 12, color: "#8B5CF6" }} />
          {overview?.promotionName || "Promotion"} Analytics
        </Title>
        <Text type="secondary">
          Performance metrics and engagement data for this promotion
        </Text>
      </div>

      {/* Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
        <TabPane
          tab={<span><BarChartOutlined /> Overview</span>}
          key="overview"
        >
          {renderOverviewTab()}
        </TabPane>
        <TabPane
          tab={<span><EyeOutlined /> Views Over Time</span>}
          key="views"
        >
          {renderViewsTab()}
        </TabPane>
        <TabPane
          tab={<span><DollarOutlined /> Conversions</span>}
          key="conversions"
        >
          {renderConversionsTab()}
        </TabPane>
        <TabPane
          tab={<span><PieChartOutlined /> Audience</span>}
          key="audience"
        >
          {renderAudienceTab()}
        </TabPane>
        <TabPane
          tab={<span><MobileOutlined /> Devices</span>}
          key="devices"
        >
          {renderDevicesTab()}
        </TabPane>
        {overview?.gamification?.enabled && (
          <TabPane
            tab={<span><TrophyOutlined /> Gamification</span>}
            key="gamification"
          >
            {renderGamificationTab()}
          </TabPane>
        )}
      </Tabs>
    </Card>
  );
};

export default PromotionAnalytics;
